export {
  VariableModeSelect,
  styleVariableModeRestored,
} from './VariableModeSelect';
export {
  getVariableModeEntries,
  type VariableModeEntry,
} from './getVariableModeEntries';

import {
  type Type,
  type MetaAttributes,
  Types,
  Component,
  Plugins,
  Expressions,
  getMeta,
} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TTypeApplication,
  TTypeConstructor,
  TRecord,
  TEmptyRecord,
  ERecord,
  EData,
  EElement,
} from '@packages/systems/wfdl-ast';
import {
  INHERIT_VARIABLE_MODE_ID,
  BASE_VARIABLE_MODE_ID,
  type VariableCollectionMap,
  type VariableCollectionId,
} from '@packages/systems/core/utils/StyleVariables';
import {
  renderModeDataAttributeName,
  renderModeDataAttributeValue,
} from '@packages/systems/core/utils/StyleVariableModesCssUtils';

const variableModeTypeName = qualify(
  Plugins.name('Basic'),
  typeName('VariableMode')
);

export function createVariableModeType(
  collectionId: VariableCollectionId
): Type {
  return TTypeApplication({
    con: TTypeConstructor(variableModeTypeName),
    arg: TRecord({[collectionId]: TEmptyRecord}),
  });
}

export const getVariableModeCollectionId = (type: Type): string | null => {
  if (!Types.isVariableModeType(type)) return null;
  const deconstructed = Types.deconstructTypeApplication(type);
  const arg = deconstructed?.args[0];
  const record = arg ? Types.getRecordFields(arg) : null;
  return record ? (Object.keys(record)[0] ?? null) : null;
};

/**
 * Author-controlled metadata attached to a `VariableMode` field type via
 * `withMeta`. Drives how mode pickers show/hide the Inherit option, relabel
 * the Base mode, and filter which collection modes are selectable.
 */
export type VariableModeMeta = {
  collectionId?: string;
  showInherit?: boolean;
  baseDisplayName?: string;
  visibleModeIds?: string[];
};

export const getVariableModeMeta = (
  meta: MetaAttributes | null
): VariableModeMeta | null => {
  if (!meta || typeof meta !== 'object') return null;
  return meta as VariableModeMeta;
};

export const getVariableModeMetaForField = (
  component: Component.ComponentType,
  fieldId: string
): VariableModeMeta | null => {
  const fields = Types.getRecordFields(Component.getDataType(component));
  const fieldType = fields?.[fieldId];
  if (!fieldType) return null;
  return getVariableModeMeta(getMeta(fieldType));
};

export function getUsedCollectionIds(
  component: Component.ComponentType,
  excludeFieldId?: string
): Set<string> {
  const fields = Types.getRecordFields(Component.getDataType(component));
  if (!fields) return new Set();

  const ids = new Set<string>();
  for (const fieldId of Object.keys(fields)) {
    if (fieldId === excludeFieldId) continue;
    const field = fields[fieldId];
    if (!field) continue;
    const collectionId = getVariableModeCollectionId(field);
    if (collectionId) {
      ids.add(collectionId);
    }
  }
  return ids;
}

export type VariableModeFieldInfo = {
  fieldId: string;
  collectionId: string;
};

export function getVariableModeFields(
  component: Component.ComponentType
): VariableModeFieldInfo[] {
  const fields = Types.getRecordFields(Component.getDataType(component));
  if (!fields) return [];

  const result: VariableModeFieldInfo[] = [];
  for (const fieldId of Object.keys(fields)) {
    const field = fields[fieldId];
    if (!field) continue;
    const collectionId = getVariableModeCollectionId(field);
    if (collectionId) {
      result.push({fieldId, collectionId});
    }
  }
  return result;
}

/**
 * Resolves variable mode attributes from an evaluated `data.variableModes` record.
 * The record maps collectionId -> evaluated modeId string.
 */
export function resolveVariableModeAttributes(
  variableModes: Record<string, unknown>,
  variableCollections: VariableCollectionMap,
  slugMap: Record<string, string>
): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const [collectionId, modeId] of Object.entries(variableModes)) {
    if (typeof modeId !== 'string') {
      continue;
    }

    const collection = variableCollections.get(collectionId);
    if (!collection) continue;

    const attrName = renderModeDataAttributeName(collection, slugMap);

    let attrValue: string;
    if (modeId === BASE_VARIABLE_MODE_ID) {
      attrValue = 'base';
    } else if (modeId === INHERIT_VARIABLE_MODE_ID) {
      attrValue = 'inherit';
    } else {
      const mode = collection.modes[modeId];
      if (!mode) continue;
      attrValue = renderModeDataAttributeValue(mode.name);
    }

    attrs[attrName] = attrValue;
  }
  return attrs;
}

/**
 * Adds a `variableModes` binding to a component's root element, so the
 * renderer can resolve mode attributes from the element itself rather than
 * walking the component's dataType.
 */
export function addVariableModeBinding(
  component: Component.ComponentType,
  fieldId: string,
  collectionId: string
): Component.ComponentType {
  const render = Component.getRender(component);
  const element = Expressions.getElement(render);
  if (!element) return component;

  const data = Expressions.getElementData(element);
  const record = Expressions.getRecord(data);
  if (!record) return component;

  const existingModes = record.variableModes
    ? Expressions.getRecord(record.variableModes) || {}
    : {};

  const updatedData = ERecord({
    ...record,
    variableModes: ERecord({
      ...existingModes,
      [collectionId]: EData([fieldId]),
    }),
  });

  const updatedElement = Expressions.setElementData(element, updatedData);
  return Component.update(component, {
    render: EElement(updatedElement),
  });
}
